<script setup lang="ts">
// Putting files on an album (HRTH-164.6): one that fits all of them (audio albums take
// audio, photo albums photos and videos), a new one, or none. A file is on one album at
// most; collections are chosen apart (CollectionAddModal).
import { computed, ref } from 'vue'
import { describeError } from '../api/http'
import { fits, TYPE_NAMES, type Collection, type CollectionType } from '../lib/collections'
import type { OpenEntry } from '../lib/libraryPlay'
import Modal from './Modal.vue'

export type Pick = { into: Collection<OpenEntry> } | { create: CollectionType; name: string } | { none: true }

const props = defineProps<{ files: OpenEntry[]; collections: Collection<OpenEntry>[]; action: (p: Pick) => Promise<unknown> }>()
const emit = defineEmits<{ close: [] }>()

const TYPE_LABEL: Record<CollectionType, string> = { audio: 'Album', visual: 'Photo album', files: 'Collection' }
const types = computed<CollectionType[]>(() => (['audio', 'visual'] as const).filter((t) => props.files.every((f) => fits(t, f.meta.kind))))
const choices = computed(() => props.collections.filter((c) => types.value.includes(c.type)))
/** Where they are now, when that's the same collection for all of them. */
const current = computed(() => {
  const ids = new Set(props.files.map((f) => props.collections.find((c) => c.items.some((i) => i.entry.id === f.entry.id))?.id ?? ''))
  return ids.size === 1 ? [...ids][0]! : null
})

const newType = ref<CollectionType>(types.value[0] ?? 'audio')
const newName = ref('')
const busy = ref(false)
const error = ref<string | null>(null)

async function run(p: Pick): Promise<void> {
  busy.value = true
  error.value = null
  try {
    await props.action(p)
    emit('close')
  } catch (e) {
    error.value = describeError(e)
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <Modal :title="files.length === 1 ? 'Put on album' : `Put ${files.length} files on`" @close="emit('close')">
    <div class="space-y-4" data-testid="collection-picker">
      <div v-if="choices.length" class="-mx-5 max-h-72 overflow-y-auto border-y border-border">
        <button
          v-for="c in choices"
          :key="c.id"
          class="flex min-h-12 w-full items-center gap-3 border-b border-border px-5 text-left last:border-b-0 hover:bg-bg-hover disabled:opacity-50"
          :disabled="busy || c.id === current"
          :data-testid="`pick-${c.name}`"
          @click="run({ into: c })"
        >
          <span class="min-w-0 flex-1">
            <span class="block truncate text-sm">{{ c.name }}</span>
            <span class="mono block truncate text-text-muted">{{ TYPE_LABEL[c.type] }}<template v-if="c.artist"> · {{ c.artist }}</template> · {{ c.items.length }}</span>
          </span>
          <span v-if="c.id === current" class="mono text-text-muted uppercase">here</span>
        </button>
      </div>
      <form class="space-y-2" @submit.prevent="newName.trim() && run({ create: newType, name: newName.trim() })">
        <label class="label" for="new-album-name">New</label>
        <div class="flex gap-2">
          <select v-if="types.length > 1" v-model="newType" class="input w-auto" aria-label="Type" data-testid="new-album-type">
            <option v-for="t in types" :key="t" :value="t">{{ TYPE_LABEL[t] }}</option>
          </select>
          <input id="new-album-name" v-model="newName" class="input min-w-0 flex-1" maxlength="200" :placeholder="`Name the ${TYPE_NAMES[newType].one}`" data-testid="new-album-name" />
          <button class="btn-primary" :disabled="busy || !newName.trim()" data-testid="new-album-save">Create</button>
        </div>
      </form>
      <button v-if="current !== ''" class="btn-ghost w-full max-md:h-11" :disabled="busy" data-testid="pick-none" @click="run({ none: true })">
        Take off {{ files.length === 1 ? 'its album' : 'their albums' }}
      </button>
      <p v-if="error" class="text-sm text-error">{{ error }}</p>
    </div>
  </Modal>
</template>
